<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import { formatIdSource } from "@core/helpers"

// ================================
// PROPS & EMITS
// ================================

const props = withDefaults(
    defineProps<{
        /** Identifiant réel du compteur (PDL ou PCE) */
        idsource?: string
        /** true = parcours PCE, false = parcours PDL (pour les libellés) */
        isPce?: boolean
    }>(),
    {
        idsource: "",
        isPce: false,
    }
)

const emit = defineEmits<{
    retry: []
    "click:quit": []
    /** Émis lorsque l'utilisateur clique sur "Signaler le problème" (support non implémenté en v6) */
    report: []
}>()

// ================================
// INTERNATIONALIZATION
// ================================

const { t } = useI18n({
    messages: {
        fr: {
            title: "Erreur",
            subtitle_pdl:
                "Impossible d'ajouter le Numéro du Point De Livraison (PDL) {idsource} car il a déjà été ajouté dans l'application.",
            subtitle_pce:
                "Impossible d'ajouter le Point de Comptage et d'Estimation pour le gaz (PCE) {idsource} car il a déjà été ajouté dans l'application.",
            helper: "Rappel : seul le titulaire du contrat de fourniture ou son représentant est autorisé à signer le consentement d'accès aux données.",
            button_retry: "Réessayer",
            button_return: "Retour",
            button_report: "Signaler le problème",
        },
    },
})

// ================================
// COMPUTED
// ================================

/** Sous-titre selon type PDL ou PCE, avec identifiant formaté. */
const getSubtitle = computed(() => {
    const formatted = formatIdSource(
        props.idsource,
        props.isPce ? "pce" : "pdl"
    )
    return t(props.isPce ? "subtitle_pce" : "subtitle_pdl", {
        idsource: formatted || props.idsource || "—",
    })
})

// ================================
// INTERACTIONS
// ================================

function onQuit(): void {
    emit("click:quit")
}

function onReport(): void {
    emit("report")
}
</script>

<template>
    <div class="source-exist-error flex flex-col gap-4">
        <BaseMessage
            severity="error"
            :pt="{
                content: 'flex flex-col justify-center',
                text: 'flex flex-col gap-4',
            }"
        >
            <div class="flex items-center justify-center gap-2">
                <i
                    class="pi pi-exclamation-circle text-xl"
                    aria-hidden="true"
                />
                <h3 class="text-lg font-semibold">
                    {{ t("title") }}
                </h3>
            </div>
            <p class="text-center text-base">
                {{ getSubtitle }}
            </p>
            <p class="text-center text-gray-600 text-sm">
                {{ t("helper") }}
            </p>
            <BaseButton
                :label="t('button_report')"
                severity="danger"
                class="min-w-[10rem] w-fit self-center"
                @click="onReport"
            />
        </BaseMessage>
        <div
            class="flex flex-col-reverse gap-3 md:flex-row md:flex-wrap md:justify-between"
        >
            <BaseButton
                :label="t('button_return')"
                severity="secondary"
                class="min-w-[10rem] w-full md:w-auto self-start"
                @click="onQuit"
            />
        </div>
    </div>
</template>
